import { Fragment, useEffect, useMemo, useState } from 'react';

import { useCustomMountTransition } from '@/components/hooks/useCustomMountTransition';
import { ButtonGroup } from '@/components/shared/buttons/button-group';
import Switch from '@/components/shared/element/switch';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Flex } from '@/components/shared/layout/flex';
import { Image } from '@/components/shared/media/image';
import { generateMobileSources } from '@/components/shared/media/image/generate-sources';
import { ImageProps } from '@/components/shared/media/image/Image.props';
import { formatPrice } from '@/components/shared/utility/format';
import { css, styled } from '@/stitches.config';
import { IMy24LineupItem } from '@/types/shared';
import { type EinsteinEngineType } from '@/types/shared/einstein/engine';
import { ITracking } from '@/types/tracking';

export const StyledLineupCard = styled(Box, {
  variants: {
    home: {
      true: {
        width: '$size-full',
        left: '50%',
        transform: 'translateX(-50%)',
      },
      false: {
        width: '$size-full',
        height: '$size-full',
        padding: '$space-4',
        boxShadow: '$shadow-300',
        overflow: 'hidden',
        transform: 'translateZ(0)',
        clipPath: 'inset(0% round 8px)',
        '&::after': {
          content: '',
          background:
            'linear-gradient(180deg, rgba(46, 46, 46, 0) 0%, rgba(46, 46, 46, 0.5) 40%, #2E2E2E 60%)',
          position: 'absolute',
          inset: 0,
          width: '$size-full',
          height: '$size-full',
          transform: 'translateZ(0)',
        },
      },
    },
    active: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      active: 'false',
      home: 'true',
      css: {
        '@lg': {
          transition: 'filter 500ms ease 500ms',
          filter: 'brightness(0.6)',
        },
      },
    },
  ],
});

const StyledEngineDetailContent = styled(Flex, {
  position: 'absolute',
  width: '$size-full',
});

const StyledEngineDetailItem = styled('li', {
  position: 'relative',
  width: '100%',
  variants: {
    home: {
      true: {
        '@lg': {
          '&:before': {
            content: '',
            position: 'absolute',
            width: '$size-full',
            height: '$size-full',
            paddingTop: '$space-10',
            paddingBottom: '$space-10',
            backgroundColor: '#1A1A1A',
            borderRadius: '$rounded-2',
            top: '-$space-4',
          },
        },
      },
      false: {},
    },
  },
});

const StyledEngineDetailList = styled('ul', {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  '@lg': {
    columnGap: '$space-6',
  },
  variants: {
    active: {
      true: {},
      false: {},
    },
    home: {
      true: {
        paddingBottom: '$space-20',
        '@lg': {
          paddingTop: '$space-4',
          paddingBottom: '$space-24',
        },
      },
      false: {
        paddingBottom: '$space-14',
        '@lg': {
          paddingBottom: 72,
        },
      },
    },
    isAnimatedInitial: {
      true: {},
    },
    engineActive: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      active: 'true',
      engineActive: 'false',
      isAnimatedInitial: 'false',
      css: {
        [`${StyledEngineDetailItem}`]: {
          transition:
            'opacity 500ms cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1) !important',
          transform: 'translateY(12px)',
          opacity: 0,
        },
      },
    },
    {
      active: 'true',
      engineActive: 'true',
      isAnimatedInitial: 'true',
      css: {
        [`${StyledEngineDetailItem}`]: {
          transition:
            'opacity 300ms cubic-bezier(0.16, 1, 0.3, 1), transform 500ms cubic-bezier(0.16, 1, 0.3, 1)',
          transform: 'translateY(0)',
          opacity: 1,
          '&:nth-child(1)': {
            transitionDelay: '0.1s !important',
          },
          '&:nth-child(2)': {
            transitionDelay: '0.16s !important',
          },
          '&:nth-child(3)': {
            transitionDelay: '0.22s !important',
          },
        },
      },
    },
    {
      active: 'true',
      engineActive: 'false',
      isAnimatedInitial: 'true',
      css: {
        [`${StyledEngineDetailItem}`]: {
          transition:
            'opacity 300ms cubic-bezier(0.16, 1, 0.3, 1), transform 500ms cubic-bezier(0.16, 1, 0.3, 1)',
          transform: 'translateY(12px)',
          opacity: 0,
          '&:nth-child(1)': {
            transitionDelay: '0.1s !important',
          },
          '&:nth-child(2)': {
            transitionDelay: '0.16s !important',
          },
          '&:nth-child(3)': {
            transitionDelay: '0.22s !important',
          },
          '&:nth-child(4)': {
            transitionDelay: '0.22s !important',
          },
          '&:nth-child(5)': {
            transitionDelay: '0.16s !important',
          },
          '&:nth-child(6)': {
            transitionDelay: '0.1s !important',
          },
        },
      },
    },
    // home inview enter - start after title at 1s
    {
      home: 'true',
      active: 'true',
      engineActive: 'true',
      isAnimatedInitial: 'false',
      css: {
        [`${StyledEngineDetailItem}`]: {
          transition:
            'opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1)',
          transform: 'translateY(0)',
          opacity: 1,
          '&:nth-child(1)': {
            transitionDelay: '1.1s',
          },
          '&:nth-child(2)': {
            transitionDelay: '1.2s',
          },
          '&:nth-child(3)': {
            transitionDelay: '1.3s',
          },
        },
      },
    },
    // home inview from
    {
      home: 'true',
      active: 'false',
      css: {
        [`${StyledEngineDetailItem}`]: {
          transition:
            'opacity 500ms cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1) 1s !important',
          transform: 'translateY(12px)',
          opacity: 0,
        },
      },
    },
  ],
});

const CssActiveTitleAnimateIn = css({
  variants: {
    active: {
      true: {},
      false: {},
    },
    inview: {
      true: {},
      false: {},
    },
    home: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      active: 'false',
      inview: 'true',
      home: 'true',
      css: {
        transition:
          'opacity 500ms cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1) 500ms',
        opacity: 0,
        transform: 'translateY(12px)',
      },
    },

    // home inview enter
    {
      active: 'true',
      inview: 'true',
      home: 'true',
      css: {
        transition:
          'opacity 1s cubic-bezier(0.16, 1, 0.3, 1) 1s, transform 1s cubic-bezier(0.16, 1, 0.3, 1) 1s',
        transform: 'translateY(0)',
        opacity: 1,
      },
    },
    // home inview from
    {
      active: 'true',
      inview: 'false',
      home: 'true',
      css: {
        transition:
          'opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1)',
        transform: 'translateY(12px)',
        opacity: 0,
      },
    },
  ],
});

const CssActiveButtonGroupAnimateIn = css({
  variants: {
    active: {
      true: {},
      false: {},
    },
    home: {
      true: {},
      false: {
        columnGap: '$space-4',
      },
    },
  },
  compoundVariants: [
    // home inview enter - start after engine detail at 1.3s
    {
      home: 'true',
      active: 'true',
      css: {
        '& a': {
          transition:
            'opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1)',
          transform: 'translateY(0)',
          opacity: 1,
          '&:nth-child(1)': {
            transitionDelay: '1.3s',
          },
          '&:nth-child(2)': {
            transitionDelay: '1.4s',
          },
        },
      },
    },
    // home inview from - end at 1.4s
    {
      home: 'true',
      active: 'false',
      css: {
        '& a': {
          transition:
            'opacity 500ms cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1) 1s',
          transform: 'translateY(12px)',
          opacity: 0,
        },
      },
    },
  ],
});

const CssModelImage = css({
  position: 'absolute !important',
  willChange: 'transform, opacity',
  variants: {
    engine: {
      '0': {},
      '1': {},
    },
    home: {
      true: {},
      false: {
        transitionDelay: '0s !important',
      },
    },
    active: {
      true: {},
      false: {},
    },
    inview: {
      true: {},
      false: {},
    },
    engineActive: {
      true: {
        transform: 'scale(1) translateZ(0px) !important',
        opacity: '1 !important',
      },
      false: {
        opacity: '0 !important',
      },
    },
    isAnimatedInitial: {
      true: {
        transitionDelay: '0.55s !important',
        transition: 'opacity 500ms ease, transform 800ms ease !important',
      },
    },
  },
  compoundVariants: [
    {
      inview: 'true',
      engine: '0',
      home: 'false',
      css: {
        transform: 'scale(1) translateZ(0px) translateX(-50px)',
      },
    },
    {
      inview: 'true',
      engine: '1',
      home: 'false',
      css: {
        transform: 'scale(1) translateZ(0px) translateX(50px)',
      },
    },
    {
      active: 'true',
      inview: 'true',
      home: 'true',
      engine: '0',
      css: {
        transform: 'scale(1) translateZ(0px) translateX(-50px)',
        '@lg': {
          transitionDelay: '0.65s',
        },
      },
    },
    {
      active: 'true',
      inview: 'true',
      home: 'true',
      engine: '1',
      css: {
        transform: 'scale(1) translateZ(0px) translateX(50px)',
        '@lg': {
          transitionDelay: '0.65s',
        },
      },
    },
    {
      active: 'false',
      inview: 'true',
      home: 'true',
      css: {
        transition: 'opacity 500ms ease, transform 800ms ease !important',
        transform: 'scale(0.77) translateZ(0px) !important',
        '@lg': {
          opacity: '0 !important',
          transition: 'opacity 500ms ease, transform 800ms ease 1s !important',
          transform:
            'scale(0.8) translateX(130px) translateY(-20px) translateZ(0px) !important',
        },
      },
    },
    {
      active: 'true',
      inview: 'false',
      home: 'true',
      engineActive: 'true',
      css: {
        transform: 'scale(0.8) translateZ(0px) !important',
        '@lg': {
          opacity: '0 !important',
          transform:
            'scale(0.8) translateX(130px) translateY(-20px) translateZ(0px) !important',
        },
      },
    },
  ],
});

export interface EinsteinEngineItem {
  key: EinsteinEngineType;
  title: string;
  power: string;
  torque: string;
  startPrice: number;
  modelImage: ImageProps;
  compareUrl?: string;
}

export interface EinsteinLineupItem extends IMy24LineupItem {
  engines: EinsteinEngineItem[];
}

interface LCVLineupCardProps {
  item: EinsteinLineupItem;
  isActive?: boolean;
  isNext?: boolean;
  isPrevious?: boolean;
  isInView?: boolean;
  home?: boolean;
  showBg?: boolean;
  dataTrack?: ITracking;
  isMobile?: boolean;
}

const EngineDetailItem = ({
  value,
  label,
  home,
}: {
  value: string;
  label: string;
  home?: boolean;
}) => {
  return (
    <StyledEngineDetailItem home={home}>
      <StyledEngineDetailContent direction="column" alignItems="center">
        <Text size="text-sub-h2" font="bold" variant="text-gray-lightest">
          {value}
        </Text>
        <Text
          size={home ? 'text-body-1' : 'text-body-2'}
          variant="text-gray-lightest"
        >
          {label}
        </Text>
      </StyledEngineDetailContent>
    </StyledEngineDetailItem>
  );
};

export const CONFIGS = {
  MAX_WIDTH: { LG: 880, XL: 1022 },
  GAP: {
    LG: 40,
  },
};
export const LCVLineupCard = ({
  item,
  home = false,
  isActive = true,
  isInView = true,
  isNext,
  isPrevious,
  showBg = true,
  dataTrack,
  isMobile = false,
}: LCVLineupCardProps) => {
  const { title, engines, buttons } = item;
  const [selectEngineIndex, setSelectEngineIndex] = useState<number>(0);
  const [isAnimatedInitial, setIsAnimatedInitial] = useState<boolean>(false);
  const engineItem = useMemo<EinsteinEngineItem>(
    () => engines[selectEngineIndex],
    [selectEngineIndex, engines]
  );
  const engineOptions = useMemo(() => {
    return engines.map((item) => ({
      label: item.title,
      value: item.key,
    }));
  }, [engines]);
  const ctaButtons = useMemo(() => {
    if (home) {
      return buttons;
    }
    const tmpButtons = buttons;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    tmpButtons[1].url = engineItem.compareUrl ?? '/compare';
    return tmpButtons;
  }, [home, buttons, engineItem]);

  const { startTransition, transitionState } = useCustomMountTransition(
    selectEngineIndex,
    300
  );

  const onEngineChange = (index: number) => {
    startTransition(() => setSelectEngineIndex(index));
  };

  useEffect(() => {
    const id = setTimeout(() => {
      setIsAnimatedInitial(isActive && isInView);
    }, 1300); // 1300 end active all animation duration
    return () => {
      clearTimeout(id);
    };
  }, [isActive, isInView]);

  return (
    <StyledLineupCard
      position="relative"
      rounded="2"
      home={home}
      active={isActive}
    >
      {home ? (
        !!showBg && (
          <Image
            rootMargin="0px 0px -1px 0px"
            src={item?.bg?.src as string}
            alt={item?.bg?.alt as string}
            width={item?.bgDesktop?.width as number}
            height={item?.bgDesktop?.height as number}
            layout="responsive"
            aspectRatio="8by3"
            sources={[
              {
                srcset: item?.bgDesktop?.src as string,
                media: { minWidth: 1024 },
                options: { width: CONFIGS.MAX_WIDTH.XL, crop: '8:3' },
              },
              ...generateMobileSources({
                mobileSrc: item?.bg?.src,
                mobileOptions: { crop: '4:5' },
              }),
            ]}
            className="bg"
            css={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%) translateZ(0)',
              width: `calc(100% - ${CONFIGS.GAP.LG}px)`,
              transition: 'opacity 1s cubic-bezier(0.16, 1, 0.3, 1)',
              clipPath: 'inset(0% round 8px)',
              cursor: 'grab',
              zIndex: '-1',
            }}
            priority={!!(isNext || isPrevious) && isInView}
          />
        )
      ) : (
        <Image
          rootMargin="0px 0px -1px 0px"
          src={item?.bg?.src as string}
          alt={item?.bg?.alt as string}
          width={item?.bgDesktop?.width as number}
          height={item?.bgDesktop?.height as number}
          layout="responsive"
          className="bg"
          sizes="(max-width: 1023px) 375px, 414px"
          useSrcSetPattern={true}
          objectFit={engines.length === 1 ? 'cover' : 'contain'}
          srcSetOptions={{
            crop: '100p,65p,offset-y0',
          }}
          css={{
            cursor: 'grab',
            position: 'absolute',
            top: '0',
            left: '0',
            minWidth: engines.length === 1 ? undefined : 'calc(100% + 35px)',
            width: engines.length === 1 ? '$size-full' : 'calc(100% + 35px)',
            height: engines.length === 1 ? '$size-full' : 'auto',
            transition:
              engines.length === 1 ? undefined : 'transform 800ms ease',
            transform:
              engines.length === 1
                ? undefined
                : selectEngineIndex === 0
                  ? 'translateX(0)'
                  : 'translateX(-35px)',
            objectPosition:
              engines.length === 1 ? 'center -96px' : 'center -12px',
            clipPath: 'inset(0% 0% 60px 0%)',
          }}
        />
      )}
      <Container
        maxWidth={home ? undefined : 'none'}
        size={home ? undefined : 'none'}
        css={{
          position: 'relative',
          zIndex: home ? undefined : 1,
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          '@lg': {
            maxWidth: home ? '976px' : undefined,
            paddingTop: home ? '5.3%' : undefined,
          },
        }}
      >
        <Box
          position="relative"
          width="full"
          css={
            home
              ? {
                  cursor: 'grab',
                  zIndex: '$1',
                  aspectRatio: home ? '16 / 9' : '3 / 2',
                  width: 'calc(100% + 48px)',
                  transform: 'translateX(-24px)',
                  marginBottom: '$space-14',
                  '@sm': {
                    width: 'calc(100% + 96px)',
                    transform: 'translateX(-48px)',
                  },
                  '@lg': {
                    width: '$size-full',
                    maxWidth: '680px',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    marginBottom: '$space-0',
                    transform: 'none',
                  },
                  '@xl': {
                    maxWidth: '780px',
                  },
                }
              : {
                  zIndex: '$1',
                  aspectRatio: '3 / 2',
                }
          }
        >
          {engines.map(
            (_, index) =>
              (index === transitionState || selectEngineIndex === index) && (
                <Image
                  key={`modelEngineImage${index}`}
                  rootMargin="10% 0px 0px 0px"
                  src={engines[index].modelImage.src}
                  alt={engines[index].modelImage.alt}
                  width={engines[index].modelImage?.width}
                  height={engines[index].modelImage?.height}
                  layout="responsive"
                  aspectRatio={home ? '16by9' : '3by2'}
                  sizes={
                    home
                      ? '(max-width: 1023px) 100vw, 828px'
                      : '(max-width: 639px) 320px, 375px'
                  }
                  srcSetOptions={{ crop: home ? '16:9' : '3:2' }}
                  useSrcSetPattern={true}
                  className={CssModelImage({
                    engine: index,
                    engineActive: selectEngineIndex === transitionState,
                    active: isActive,
                    inview: isInView,
                    home: home,
                    isAnimatedInitial,
                  })}
                />
              )
          )}
        </Box>
        <Flex
          direction={
            home
              ? {
                  '@initial': 'column',
                  '@lg': 'row',
                }
              : 'column'
          }
          justifyContent={{
            '@initial': 'start',
            '@lg': home ? 'between' : 'start',
          }}
          alignItems="center"
          columnGap={home ? '8' : '4'}
          rowGap={home ? { '@initial': '4', '@lg': '0' } : '4'}
          className={CssActiveTitleAnimateIn({
            active: isActive,
            inview: isInView,
            home,
          })}
          css={
            home
              ? {
                  marginBottom: '$space-8',
                  '@lg': {
                    marginBottom: '$space-6',
                  },
                }
              : {
                  marginBottom: '$space-4',
                }
          }
        >
          {(!isMobile || !home) && (
            <Text
              size={home ? 'text-h2' : 'text-h3'}
              variant="text-gray-lightest"
            >
              {title}
            </Text>
          )}
          <Switch
            name={`switch${title}${engineItem.key}`}
            options={engineOptions}
            value={engineItem.key}
            size={home ? 'md' : 'sm'}
            onSwitch={(_: string, index: number) => onEngineChange(index)}
            css={{
              pe: transitionState !== selectEngineIndex ? 'none' : 'all',
            }}
            dataTracking={
              dataTrack
                ? {
                    ...dataTrack,
                    dataTrackValue: `${item?.title}`,
                  }
                : undefined
            }
            dataTest="lineup_engine"
          />
        </Flex>

        <StyledEngineDetailList
          active={!!isActive && isInView}
          engineActive={selectEngineIndex === transitionState}
          home={home}
          isAnimatedInitial={isAnimatedInitial}
        >
          {engines.map(
            (_, index) =>
              (index === transitionState || selectEngineIndex === index) && (
                <Fragment key={`engineDetailItem${index}`}>
                  <EngineDetailItem
                    label="แรงม้า"
                    value={engines[index].power}
                    home={home}
                  />
                  <EngineDetailItem
                    label="แรงบิด (Nm)"
                    value={engines[index].torque}
                    home={home}
                  />
                  <EngineDetailItem
                    label="บาท"
                    value={formatPrice(engines[index].startPrice, false)}
                    home={home}
                  />
                </Fragment>
              )
          )}
        </StyledEngineDetailList>

        <ButtonGroup
          buttons={ctaButtons}
          size={home ? 'lg' : 'md'}
          isFull={!home}
          isMinWidth={home}
          wrap="noWrap"
          justify="center"
          tracking={{
            dataTrack: dataTrack?.dataTrack as string,
            dataTrackSection: dataTrack?.dataTrackSection,
            dataTrackValue: item?.title,
          }}
          test={{
            dataTest: 'lineup_item_ctaButton',
          }}
          className={CssActiveButtonGroupAnimateIn({
            active: isActive && isInView,
            home,
          })}
        />
      </Container>
    </StyledLineupCard>
  );
};
